import { startOfDay } from "date-fns";

/**
 * Typed-date parsing for `DatePicker`.
 *
 * Input is read in the order the field displays, day first, in every UI
 * language. Accepted shapes:
 * - `dd.MM.yyyy`, `dd/MM/yyyy`, `dd-MM-yyyy`, each also with one-digit day or
 *   month, a two-digit year, or no year (`1.2.26`, `29/8`, `29-8-2026`, `29.8.`).
 * - ISO `yyyy-MM-dd` (also `yyyy-M-d`).
 * Anything else, including a month-first reading, is rejected rather than
 * guessed.
 *
 * The function is pure: `today` is injected and supplies the year for input
 * that omits it and the anchor for the accepted year range.
 */

/** Excel rule: two-digit years 00-29 map to 2000-2029, 30-99 map to 1930-1999. */
export const TWO_DIGIT_YEAR_PIVOT = 30;
/** Accepted resolved years: today's year - 100 ... today's year + 20 (unchanged range). */
export const TYPED_DATE_YEARS_BACK = 100;
export const TYPED_DATE_YEARS_AHEAD = 20;

export interface ParseTypedDateOptions {
  /** Earliest accepted day (inclusive), compared at day granularity. */
  minDate?: Date;
}

const ISO_PATTERN = /^(\d{4})-(\d{1,2})-(\d{1,2})$/;
// The back-reference forces one separator kind, so `1.2/26` never matches.
const SHORTHAND_PATTERN = /^(\d{1,2})([./-])(\d{1,2})(?:\2(\d*))?$/;

/** Resolves the year part of a shorthand, or `null` for an unsupported length. */
function resolveYear(yearPart: string | undefined, today: Date): number | null {
  if (!yearPart) return today.getFullYear();
  const n = Number(yearPart);
  if (yearPart.length === 2) {
    return n < TWO_DIGIT_YEAR_PIVOT ? 2000 + n : 1900 + n;
  }
  if (yearPart.length === 4) return n;
  return null;
}

/** Local-midnight date for y/m/d, or `null` when the parts roll over. */
function buildLocalDate(year: number, month: number, day: number): Date | null {
  const date = new Date(year, month - 1, day);
  if (
    date.getFullYear() !== year ||
    date.getMonth() !== month - 1 ||
    date.getDate() !== day
  ) {
    return null;
  }
  return date;
}

export function parseTypedDate(
  input: string,
  today: Date,
  options?: ParseTypedDateOptions,
): Date | null {
  const s = input.trim();
  if (!s) return null;

  let year: number;
  let day: number;
  let month: number;

  const iso = ISO_PATTERN.exec(s);
  if (iso) {
    year = Number(iso[1]);
    month = Number(iso[2]);
    day = Number(iso[3]);
  } else {
    const match = SHORTHAND_PATTERN.exec(s);
    if (!match) return null;
    const resolved = resolveYear(match[4], today);
    if (resolved === null) return null;
    year = resolved;
    day = Number(match[1]);
    month = Number(match[3]);
  }

  // Check the year range first, so a year below 100 never reaches the Date
  // constructor's 1900 mapping.
  const currentYear = today.getFullYear();
  if (
    year < currentYear - TYPED_DATE_YEARS_BACK ||
    year > currentYear + TYPED_DATE_YEARS_AHEAD
  ) {
    return null;
  }

  const date = buildLocalDate(year, month, day);
  if (!date) return null;

  if (options?.minDate && date < startOfDay(options.minDate)) {
    return null;
  }

  return date;
}
